import { useState, useEffect, useCallback, Fragment } from "react";
import {
  Loader2, Plus, Trash2, GripVertical, ChevronDown, Save, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { MoneyField } from "./commercial/CommercialFields";
import { PLAN_LABELS, currencySymbol } from "../lib/commercial";
import { COUNTRIES, getCountry } from "../lib/countries";

const StatusCell = ({ status, code }) => {
  if (!status) return <span className="text-slate-300 text-xs">…</span>;
  return status.complete ? (
    <span data-testid={`region-pricing-status-${code}`} className="inline-flex items-center gap-1 text-emerald-600 text-sm">
      <CheckCircle2 className="w-4 h-4" /> Tarification complète
    </span>
  ) : (
    <span data-testid={`region-pricing-status-${code}`} className="inline-flex items-center gap-1 text-amber-600 text-sm">
      <AlertTriangle className="w-4 h-4" /> Tarification incomplète
    </span>
  );
};

// Matrice tarifaire d'une région (pliée sous la ligne).
const PricingMatrix = ({ code, currency, onChanged }) => {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/commercial/regions/${code}/pricing`);
    setData(data);
    const e = {};
    (data.plans || []).forEach((p) => {
      e[p.plan_key] = {
        base_price_minor: p.base_price_minor,
        additional_member_price_minor: p.additional_member_price_minor,
        requires_quote: !!p.requires_quote,
      };
    });
    setEdits(e);
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const setField = (pk, f, v) => setEdits((p) => ({ ...p, [pk]: { ...p[pk], [f]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/commercial/regions/${code}/pricing`, { plans: edits });
      toast.success("Tarifs enregistrés.");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'enregistrement des tarifs.");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div data-testid={`region-pricing-matrix-${code}`} className="pt-1">
      <p className="text-xs text-slate-500 mb-3">
        Tarifs en {currencySymbol(currency)} ({currency}). La devise est celle de la région et n'est pas modifiable ici.
      </p>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm min-w-[620px]">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2 font-medium">Plan</th>
              <th className="px-3 py-2 font-medium">Prix de base</th>
              <th className="px-3 py-2 font-medium">Prix / utilisateur suppl.</th>
              <th className="px-3 py-2 font-medium">Sur devis</th>
            </tr>
          </thead>
          <tbody>
            {(data.plans || []).map((p) => {
              const e = edits[p.plan_key] || {};
              const isFree = p.billing_model === "free";
              const quote = !!e.requires_quote || p.plan_requires_quote;
              const perUser = p.billing_model === "per_user";
              return (
                <tr key={p.plan_key} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{p.name || PLAN_LABELS[p.plan_key]}</div>
                    {p.plan_requires_quote && <div className="text-[11px] text-slate-400">plan sur devis</div>}
                  </td>
                  <td className="px-3 py-2 w-[34%]">
                    {isFree ? <span className="text-slate-400 text-xs">Gratuit (0)</span> : quote ? <span className="text-slate-400 text-xs">—</span> : (
                      <MoneyField key={`b-${code}-${p.plan_key}`} testid={`price-base-${code}-${p.plan_key}`}
                        initialMinor={e.base_price_minor} currency={currency}
                        onMinorChange={(m) => setField(p.plan_key, "base_price_minor", m)} />
                    )}
                  </td>
                  <td className="px-3 py-2 w-[34%]">
                    {perUser && !quote && !isFree ? (
                      <MoneyField key={`a-${code}-${p.plan_key}`} testid={`price-add-${code}-${p.plan_key}`}
                        initialMinor={e.additional_member_price_minor} currency={currency}
                        onMinorChange={(m) => setField(p.plan_key, "additional_member_price_minor", m)} />
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {isFree ? <span className="text-slate-400 text-xs">—</span> : (
                      <Switch data-testid={`price-quote-${code}-${p.plan_key}`} checked={quote}
                        onCheckedChange={(v) => setField(p.plan_key, "requires_quote", v)} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3">
        <StatusCell status={data.status} code={code} />
        <Button data-testid={`save-region-pricing-${code}`} onClick={save} disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer les tarifs
        </Button>
      </div>
    </div>
  );
};

export const CommercialRegions = () => {
  const [regions, setRegions] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [dragCode, setDragCode] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [adding, setAdding] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [ccTarget, setCcTarget] = useState(null);          // région dont on édite les pays couverts
  const [ccSel, setCcSel] = useState([]);
  const [ccSaving, setCcSaving] = useState(false);
  const [ccSearch, setCcSearch] = useState("");

  const regionCountries = (r) => (r.country_codes && r.country_codes.length ? r.country_codes : [r.country_code || r.id]);

  const openCountries = (r) => { setCcTarget(r); setCcSel(regionCountries(r)); setCcSearch(""); };
  const toggleCc = (code) => setCcSel((s) => (s.includes(code) ? s.filter((x) => x !== code) : [...s, code]));
  const saveCountries = async () => {
    if (!ccTarget || ccSel.length === 0) return;
    setCcSaving(true);
    try {
      await api.put(`/admin/commercial/regions/${ccTarget.id}`, {
        currency: ccTarget.currency, active: !!ccTarget.active,
        display_order: ccTarget.display_order || 0, name: ccTarget.name,
        country_codes: ccSel,
      });
      toast.success(`Pays de la région ${ccTarget.name} mis à jour.`);
      setCcTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec (conflit de pays ?).");
    } finally { setCcSaving(false); }
  };

  const loadStatuses = useCallback(async (regs) => {
    const entries = await Promise.all((regs || []).map((r) =>
      api.get(`/admin/commercial/regions/${r.id}/pricing`).then((res) => [r.id, res.data.status]).catch(() => [r.id, null])));
    setStatuses(Object.fromEntries(entries));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/commercial/regions");
      const regs = data.regions || [];
      setRegions(regs);
      loadStatuses(regs);
    } catch (e) { toast.error("Impossible de charger les régions."); }
    finally { setLoading(false); }
  }, [loadStatuses]);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (code, active) => {
    try {
      await api.patch(`/admin/commercial/regions/${code}/active`, { active });
      setRegions((rs) => rs.map((r) => (r.id === code ? { ...r, active } : r)));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec."); }
  };

  const persistOrder = async (ordered) => {
    setRegions(ordered);
    try { await api.put("/admin/commercial/regions-reorder", { order: ordered.map((r) => r.id) }); }
    catch (e) { toast.error("Échec de la réorganisation."); load(); }
  };

  const onDrop = (targetCode) => {
    if (!dragCode || dragCode === targetCode) return;
    const arr = [...regions];
    const from = arr.findIndex((r) => r.id === dragCode);
    const to = arr.findIndex((r) => r.id === targetCode);
    if (from < 0 || to < 0) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setDragCode(null);
    persistOrder(arr);
  };

  const presentCodes = new Set(regions.map((r) => r.id));
  const available = COUNTRIES.filter((c) => !presentCodes.has(c.code));
  const selectedCountry = getCountry(addCode);

  const addRegion = async () => {
    const c = selectedCountry;
    if (!c) return;
    setAdding(true);
    try {
      await api.post("/admin/commercial/regions", {
        code: c.code, name: c.name, currency: c.currency, phone_code: c.phone,
        default_locale: c.locale, locale_label: c.localeLabel, country_code: c.code, flag: c.flag,
      });
      toast.success(`Région ${c.name} ajoutée (inactive).`);
      setAddOpen(false); setAddCode("");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'ajout de la région.");
    } finally { setAdding(false); }
  };

  const doDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/commercial/regions/${delTarget.id}`);
      toast.success(`Région ${delTarget.name} supprimée.`);
      setDelTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de la suppression.");
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid="commercial-regions" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-slate-500">Glissez les lignes pour réordonner. La devise, l'indicatif et la langue proviennent du pays.</p>
        <Button data-testid="add-region-btn" onClick={() => setAddOpen(true)} className="w-full sm:w-auto shrink-0 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter une région
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="glass rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]" data-testid="regions-table">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3 font-medium w-8"></th>
                  <th className="px-3 py-3 font-medium">Région</th>
                  <th className="px-3 py-3 font-medium">Pays couverts</th>
                  <th className="px-3 py-3 font-medium">Devise</th>
                  <th className="px-3 py-3 font-medium">Indicatif</th>
                  <th className="px-3 py-3 font-medium">Langue</th>
                  <th className="px-3 py-3 font-medium">Active</th>
                  <th className="px-3 py-3 font-medium">Tarification</th>
                  <th className="px-3 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {regions.map((r) => {
                  const isOpen = open === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr data-testid={`region-row-${r.id}`}
                        draggable onDragStart={() => setDragCode(r.id)}
                        onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(r.id)}
                        className={`border-t border-slate-100 ${isOpen ? "bg-emerald-50/40" : ""} ${dragCode === r.id ? "opacity-50" : ""}`}>
                        <td className="px-3 py-3 cursor-grab text-slate-400" data-testid={`region-drag-${r.id}`}><GripVertical className="w-4 h-4" /></td>
                        <td className="px-3 py-3">
                          <span className="font-medium text-slate-800" data-testid={`region-flag-${r.id}`}>
                            <span className="mr-1.5 text-base">{r.flag || "🏳️"}</span>{r.name}
                          </span>
                          <span className="ml-1 text-[11px] text-slate-400">{r.id}</span>
                        </td>
                        <td className="px-3 py-3" data-testid={`region-countries-${r.id}`}>
                          <button onClick={() => openCountries(r)} data-testid={`region-countries-edit-${r.id}`}
                            className="inline-flex items-center gap-1.5 text-left hover:underline">
                            <Badge variant="outline" className="border-slate-200">{regionCountries(r).length} pays</Badge>
                            <span className="text-xs text-slate-500 max-w-[160px] truncate">{regionCountries(r).join(", ")}</span>
                          </button>
                        </td>
                        <td className="px-3 py-3" data-testid={`region-currency-${r.id}`}>{r.currency}</td>
                        <td className="px-3 py-3" data-testid={`region-phone-${r.id}`}>{r.phone_code || "—"}</td>
                        <td className="px-3 py-3" data-testid={`region-locale-${r.id}`}>{r.locale_label || r.default_locale || "—"}</td>
                        <td className="px-3 py-3">
                          <Switch data-testid={`region-active-toggle-${r.id}`} checked={!!r.active} onCheckedChange={(v) => toggleActive(r.id, v)} />
                        </td>
                        <td className="px-3 py-3">
                          <button data-testid={`region-pricing-toggle-${r.id}`} onClick={() => setOpen((c) => (c === r.id ? null : r.id))}
                            className="inline-flex items-center gap-1.5 hover:underline">
                            <StatusCell status={statuses[r.id]} code={r.id} />
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          </button>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <Button data-testid={`region-delete-${r.id}`} size="icon" variant="outline"
                            className="border-slate-200 h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setDelTarget(r)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-slate-100 bg-white">
                          <td colSpan={9} className="px-4 py-4">
                            <PricingMatrix code={r.id} currency={r.currency} onChanged={() => loadStatuses(regions)} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ajout de région */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-region-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter une région</DialogTitle>
            <DialogDescription>Choisissez un pays ou territoire. Devise, indicatif et langue sont remplis automatiquement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={addCode} onValueChange={setAddCode}>
              <SelectTrigger data-testid="add-region-select"><SelectValue placeholder="Sélectionner un pays / territoire" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {available.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`add-region-option-${c.code}`}>
                    {c.flag} {c.name} — {c.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCountry && (
              <div className="grid grid-cols-2 gap-2 text-sm rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="add-region-preview">
                <div><span className="text-slate-400">Drapeau :</span> {selectedCountry.flag}</div>
                <div><span className="text-slate-400">Devise :</span> {selectedCountry.currency}</div>
                <div><span className="text-slate-400">Indicatif :</span> {selectedCountry.phone}</div>
                <div><span className="text-slate-400">Langue :</span> {selectedCountry.localeLabel}</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button data-testid="add-region-confirm" onClick={addRegion} disabled={!selectedCountry || adding}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Édition des pays couverts (multi-pays) */}
      <Dialog open={!!ccTarget} onOpenChange={(o) => !o && setCcTarget(null)}>
        <DialogContent data-testid="region-countries-dialog">
          <DialogHeader>
            <DialogTitle>Pays couverts — {ccTarget?.name}</DialogTitle>
            <DialogDescription>
              Sélectionnez un ou plusieurs pays (ISO). Au moins un pays requis. Un pays déjà couvert par une
              autre région active empêchera l'activation / la publication (contrôle backend).
            </DialogDescription>
          </DialogHeader>
          <input
            data-testid="region-countries-search"
            value={ccSearch}
            onChange={(e) => setCcSearch(e.target.value)}
            placeholder="Rechercher un pays…"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
            {COUNTRIES.filter((c) => c.name.toLowerCase().includes(ccSearch.toLowerCase()) || c.code.includes(ccSearch.toUpperCase()))
              .map((c) => (
                <label key={c.code} data-testid={`region-country-opt-${c.code}`}
                  className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={ccSel.includes(c.code)} onChange={() => toggleCc(c.code)} />
                  <span>{c.flag} {c.name}</span>
                  <span className="ml-auto text-xs text-slate-400">{c.code}</span>
                </label>
              ))}
          </div>
          <p className="text-xs text-slate-500">{ccSel.length} pays sélectionné(s).</p>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setCcTarget(null)}>Annuler</Button>
            <Button data-testid="region-countries-save" onClick={saveCountries} disabled={ccSaving || ccSel.length === 0}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {ccSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer les pays
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation suppression */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="confirm-delete-region-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer la région</DialogTitle>
            <DialogDescription>
              {delTarget?.name} sera retirée de la configuration courante. Les publications déjà effectuées
              conservent leur snapshot historique intact.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setDelTarget(null)}>Annuler</Button>
            <Button data-testid="confirm-delete-region-ok" onClick={doDelete} disabled={deleting} className="bg-red-600 hover:bg-red-500 text-white font-semibold">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
